(() => {
  'use strict';

  const normalise = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

  const normaliseCountry = (value) => {
    const country = normalise(value).toLocaleLowerCase();
    if (['at', 'austria', 'österreich', 'oesterreich'].includes(country)) return 'at';
    if (['de', 'germany', 'deutschland'].includes(country)) return 'de';
    return country;
  };

  // 鲁棒匹配键: 小写 + 只保留字母数字（Unicode）。
  // "2000Wp 🔥" / "2000 Wp" / "2.000wp" → 全部归一成 "2000wp"，
  // 表情、空格、标点、大小写差异不再导致组静默失配。
  const normaliseKey = (value) => String(value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

  class ProductSetConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.configElement = this.querySelector('[data-product-set-config]');
      this.dialog = this.querySelector('[data-product-set-dialog]');
      this.groupsElement = this.querySelector('[data-product-set-groups]');
      this.emptyElement = this.querySelector('[data-product-set-empty]');
      this.fieldsElement = this.querySelector('[data-product-set-form-fields]');
      this.trigger = this.querySelector('[data-product-set-dialog-open]');
      this.previewsElement = this.querySelector('[data-product-set-previews]');
      this.includedElement = this.querySelector('[data-product-set-included]');
      this.inlineGroupsElement = this.querySelector('[data-product-set-inline-groups]');

      this.totalElements = this.querySelectorAll('[data-product-set-total]');

      // The redesign always renders its product summary. Products without set
      // configuration intentionally have no dialog payload and need no JS.
      if (!this.configElement) {
        this.toggleAttribute('data-product-set-unconfigured', true);
        return;
      }

      try {
        this.config = JSON.parse(this.configElement?.textContent || '');
      } catch (error) {
        console.error('[Product set] Invalid configuration JSON.', error);
        this.trigger?.setAttribute('hidden', '');
        this.toggleAttribute('data-product-set-unconfigured', true);
        return;
      }

      if (!this.dialog || !this.groupsElement || !Array.isArray(this.config.groups)) {
        this.trigger?.setAttribute('hidden', '');
        this.toggleAttribute('data-product-set-unconfigured', true);
        return;
      }

      this.formId = this.dataset.productFormId;
      this.basePriceCents = Number(this.config.basePriceCents) || 0;
      this.variantOptions = Array.isArray(this.config.selectedOptions)
        ? this.config.selectedOptions.slice()
        : [];
      this.prepareCountryVariants();
      this.applyCountryVariants();
      this.committedSelections = new Map();
      this.draftSelections = new Map();
      this.inlineExpandedGroups = new Set();
      this.currentGroups = [];
      this.activeTrigger = null;
      this.otherAddonCents = 0;
      this.productInfo = this.closest('product-info');

      this.moneyFormatter = new Intl.NumberFormat(this.config.locale || 'de-DE', {
        style: 'currency',
        currency: this.config.currency || 'EUR',
        currencyDisplay: 'symbol'
      });

      this.onClick = this.handleClick.bind(this);
      this.onDialogCancel = this.handleDialogCancel.bind(this);
      this.onDialogClose = this.handleDialogClose.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onPriceUpdated = this.handlePriceUpdated.bind(this);

      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('cancel', this.onDialogCancel);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.productInfo?.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('product-price:updated', this.onPriceUpdated);

      this.initialised = true;
      this.activateProfile({ reset: true });
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      this.removeEventListener('click', this.onClick);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.productInfo?.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('product-price:updated', this.onPriceUpdated);
      this.initialised = false;
    }

    get optionIndex() {
      return (this.config.optionNames || []).findIndex(
        (name) => normalise(name).toLocaleLowerCase() === normalise(this.config.optionName).toLocaleLowerCase()
      );
    }

    get leistungValue() {
      return this.optionIndex >= 0 ? normaliseKey(this.variantOptions[this.optionIndex]) : '';
    }

    get countryOptionIndex() {
      return (this.config.optionNames || []).findIndex(
        (name) => normalise(name).toLocaleLowerCase() === 'lieferland'
      );
    }

    get deliveryCountry() {
      return this.countryOptionIndex >= 0
        ? normaliseCountry(this.variantOptions[this.countryOptionIndex])
        : '';
    }

    prepareCountryVariants() {
      this.config.groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          item.baseVariant = {
            variantId: item.variantId,
            priceCents: Number(item.priceCents) || 0,
            available: Boolean(item.available)
          };
        });
      });
    }

    applyCountryVariants() {
      const country = this.deliveryCountry;

      this.config.groups.forEach((group) => {
        (group.items || []).forEach((item) => {
          const baseVariant = item.baseVariant || {
            variantId: item.variantId,
            priceCents: Number(item.priceCents) || 0,
            available: Boolean(item.available)
          };
          const variants = Array.isArray(item.countryVariants) ? item.countryVariants : [];

          if (!country || variants.length === 0) {
            item.variantId = baseVariant.variantId;
            item.priceCents = item.isDefault ? 0 : baseVariant.priceCents;
            item.available = item.isDefault ? true : baseVariant.available;
            return;
          }

          const matchedVariant = variants.find(
            (variant) => normaliseCountry(variant.country) === country
          );

          item.variantId = matchedVariant?.variantId || null;
          item.priceCents = item.isDefault ? 0 : (Number(matchedVariant?.priceCents) || 0);
          item.available = item.isDefault ? true : Boolean(matchedVariant?.available);
        });
      });
    }

    activateProfile({ reset = false } = {}) {
      const previousGroupIds = new Set(this.currentGroups.map((group) => String(group.id)));
      const leistung = this.leistungValue;

      // Leistung value 支持逗号分隔多个值: "2000Wp – 4 Module 🔥, 3000Wp – 6 Module"
      // 任一值命中当前选项即显示该组；单个值的旧数据天然兼容。
      const groupKeys = (value) => String(value ?? '').split(',').map((part) => normaliseKey(part)).filter(Boolean);

      this.currentGroups = this.config.groups.filter(
        (group) => groupKeys(group.leistungValue).includes(leistung) && Array.isArray(group.items) && group.items.length
      );

      const nextGroupIds = new Set(this.currentGroups.map((group) => String(group.id)));
      const profileChanged = reset || previousGroupIds.size !== nextGroupIds.size ||
        [...previousGroupIds].some((id) => !nextGroupIds.has(id));

      if (profileChanged) {
        this.committedSelections = this.createDefaultSelections();
      } else {
        this.committedSelections = this.sanitiseSelections(this.committedSelections);
      }

      this.draftSelections = new Map(this.committedSelections);
      const hasActiveGroups = this.currentGroups.length > 0;
      this.trigger?.toggleAttribute('hidden', !hasActiveGroups);
      this.toggleAttribute('data-product-set-unconfigured', !hasActiveGroups);

      // Build the potentially large option grid only when the dialog is open.
      // The overview needs only the committed preview images.
      if (this.dialog.open) {
        this.renderGroups(this.committedSelections);
      } else {
        this.groupsElement.replaceChildren();
      }
      this.renderPreviews();
      this.renderInlinePanel();
      this.syncFormFields();
      this.updateSelectedOptionsText();
      this.setAddonCents(this.getAddonCents(this.committedSelections));

      if (this.dialog.open && !hasActiveGroups) this.cancelDraft();
    }

    createDefaultSelections() {
      const selections = new Map();
      this.currentGroups.forEach((group) => {
        const item = group.items.find((candidate) => candidate.isDefault && candidate.available) ||
          group.items.find((candidate) => candidate.available);
        if (item) selections.set(String(group.id), String(item.id));
      });
      return selections;
    }

    sanitiseSelections(source) {
      const selections = new Map();
      this.currentGroups.forEach((group) => {
        const selectedId = source.get(String(group.id));
        const selected = group.items.find(
          (item) => String(item.id) === String(selectedId) && item.available
        );
        const fallback = group.items.find((item) => item.isDefault && item.available) ||
          group.items.find((item) => item.available);
        const item = selected || fallback;
        if (item) selections.set(String(group.id), String(item.id));
      });
      return selections;
    }

    getSelectedEntries(selections = this.committedSelections) {
      return this.currentGroups.flatMap((group) => {
        const selectedId = selections.get(String(group.id));
        const item = group.items.find((candidate) => String(candidate.id) === String(selectedId));
        return item ? [{ group, item }] : [];
      });
    }

    getItemQuantity(item) {
      return Math.max(1, Number(item.quantity ?? item.quantityPerSet) || 1);
    }

    isFixedGroup(group) {
      return Array.isArray(group?.items) && group.items.length === 1;
    }

    getSelectableGroups() {
      return this.currentGroups.filter((group) => !this.isFixedGroup(group));
    }

    formatQuantity(item) {
      return `× ${this.getItemQuantity(item)}`;
    }

    renderInlinePanel() {
      this.renderIncludedItems();
      this.renderInlineGroups();
    }

    renderIncludedItems() {
      if (!this.includedElement) return;

      const fragment = document.createDocumentFragment();
      this.getSelectedEntries()
        .filter(({ group }) => this.isFixedGroup(group))
        .forEach(({ item }) => {
          const row = document.createElement('li');
          row.className = 'product-set-overview__included-item';

          const title = document.createElement('span');
          title.className = 'product-set-overview__included-title';
          title.textContent = item.title || '';

          const qty = document.createElement('span');
          qty.className = 'product-set-overview__included-qty';
          qty.textContent = this.formatQuantity(item);

          row.append(title, qty);
          fragment.append(row);
        });

      const hasItems = fragment.childNodes.length > 0;
      this.includedElement.replaceChildren(fragment);
      this.includedElement.hidden = !hasItems;
    }

    renderInlineGroups() {
      if (!this.inlineGroupsElement) return;

      const fragment = document.createDocumentFragment();
      this.getSelectableGroups().forEach((group, groupIndex) => {
        const section = document.createElement('section');
        section.className = 'product-set-inline__group';

        const title = document.createElement('h4');
        title.className = 'product-set-inline__group-title';
        title.id = `ProductSetInline-${this.formId || 'form'}-${groupIndex}`;
        title.textContent = 'DC-Kabellänge(MPPT-Seite) wählen';
        section.setAttribute('aria-labelledby', title.id);

        const choices = document.createElement('div');
        choices.className = 'product-set-inline__choices';
        choices.setAttribute('role', 'radiogroup');
        group.items.forEach((item) => {
          choices.append(this.createInlineChoice(group, item));
        });

        section.append(title, choices);

        if (group.items.length > 3) {
          const selectedId = this.committedSelections.get(String(group.id));
          const selectedIndex = group.items.findIndex((item) => String(item.id) === String(selectedId));
          const expanded = this.inlineExpandedGroups.has(String(group.id)) || selectedIndex >= 3;
          if (expanded) this.inlineExpandedGroups.add(String(group.id));
          else choices.classList.add('is-collapsed');
          section.append(this.createInlineMoreButton(group, expanded));
        }

        fragment.append(section);
      });

      const hasGroups = fragment.childNodes.length > 0;
      this.inlineGroupsElement.replaceChildren(fragment);
      this.inlineGroupsElement.hidden = !hasGroups;
    }

    createInlineChoice(group, item) {
      const selected = this.committedSelections.get(String(group.id)) === String(item.id);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `product-set-inline__choice${selected ? ' is-selected' : ''}${item.preorder ? ' is-preorder' : ''}`;
      button.dataset.productSetInlineChoice = '';
      button.dataset.groupId = String(group.id);
      button.dataset.itemId = String(item.id);
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(selected));
      button.disabled = !item.available;

      const imageWrap = document.createElement('span');
      imageWrap.className = 'product-set-inline__choice-image';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 80;
        image.height = 80;
        image.loading = 'lazy';
        image.decoding = 'async';
        imageWrap.append(image);
      }

      const copy = document.createElement('span');
      copy.className = 'product-set-inline__choice-copy';

      const label = document.createElement('span');
      label.className = 'product-set-inline__choice-label';
      label.textContent = item.title || '';

      const qty = document.createElement('span');
      qty.className = 'product-set-inline__choice-qty';
      qty.textContent = this.formatQuantity(item);
      copy.append(label, qty);

      const meta = document.createElement('span');
      meta.className = 'product-set-inline__choice-meta';

      if (item.preorder) {
        const badge = document.createElement('span');
        badge.className = 'product-set-inline__choice-preorder';
        badge.textContent = 'Vorbestellung';
        meta.append(badge);
      }

      const price = document.createElement('span');
      price.className = 'product-set-inline__choice-price';
      const total = Math.max(0, Number(item.priceCents) || 0) * this.getItemQuantity(item);
      price.textContent = total === 0 ? '+ 0 €' : `+ ${this.formatMoney(total)}`;
      meta.append(price);

      button.append(imageWrap, copy, meta);

      const wrap = document.createElement('div');
      wrap.className = 'product-set-inline__choice-item';
      wrap.append(button);

      const message = String(item.preorderMessage || '').trim();
      if (item.preorder && message) {
        const hint = document.createElement('p');
        hint.className = 'product-set-inline__choice-message';
        hint.textContent = message;
        hint.hidden = !selected;
        wrap.append(hint);
      }

      return wrap;
    }

    createInlineMoreButton(group, expanded) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-set-inline__more';
      button.dataset.productSetInlineMore = '';
      button.dataset.groupId = String(group.id);
      button.setAttribute('aria-expanded', String(Boolean(expanded)));

      const label = document.createElement('span');
      label.dataset.productSetInlineMoreLabel = '';
      label.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';

      button.append(label);
      button.insertAdjacentHTML('beforeend', `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" class="product-set-inline__more-chevron">
          <path d="M7.99953 8.78105L11.2994 5.4812L12.2422 6.42401L7.99953 10.6667L3.75694 6.42401L4.69973 5.4812L7.99953 8.78105Z" fill="#00A5C3"></path>
        </svg>
      `.trim());
      return button;
    }

    toggleInlineMore(button) {
      const choices = button.previousElementSibling;
      if (!choices?.classList.contains('product-set-inline__choices')) return;

      const expanded = choices.classList.contains('is-collapsed');
      choices.classList.toggle('is-collapsed', !expanded);
      button.setAttribute('aria-expanded', String(expanded));

      const label = button.querySelector('[data-product-set-inline-more-label]');
      if (label) label.textContent = expanded ? 'Weniger anzeigen' : 'Mehr anzeigen';

      const groupId = String(button.dataset.groupId || '');
      if (!groupId) return;
      if (expanded) this.inlineExpandedGroups.add(groupId);
      else this.inlineExpandedGroups.delete(groupId);
    }

    commitInlineChoice(groupId, itemId) {
      this.committedSelections.set(String(groupId), String(itemId));
      this.committedSelections = this.sanitiseSelections(this.committedSelections);
      this.draftSelections = new Map(this.committedSelections);
      this.renderInlineGroups();
      this.renderPreviews();
      this.syncFormFields();
      this.setAddonCents(this.getAddonCents(this.committedSelections));
      this.dispatchEvent(new CustomEvent('product-set:change', {
        bubbles: true,
        detail: { selections: this.getSelectedEntries() }
      }));
    }

    getAddonCents(selections = this.committedSelections) {
      return this.getSelectedEntries(selections).reduce(
        (sum, { item }) => sum + (Math.max(0, Number(item.priceCents) || 0) * this.getItemQuantity(item)),
        0
      );
    }

    getPaidUpgradeItems() {
      return this.getSelectedEntries().flatMap(({ group, item }) => {
        // 挂了变体就生成订单行: 付费升级件(>0€) 和 0元赠送变体(默认项挂了0元变体)。
        // 无变体的纯赠送条目不生成行(只留在主商品属性记录里)。
        if (!item.variantId || !item.available) return [];
        return [{
          id: Number(item.variantId),
          quantityPerSet: this.getItemQuantity(item),
          groupId: String(group.id),
          groupTitle: group.title,
          itemId: String(item.id),
          itemTitle: item.title
        }];
      });
    }

    renderGroups(selections) {
      const fragment = document.createDocumentFragment();

      this.currentGroups.forEach((group, groupIndex) => {
        const section = document.createElement('section');
        section.className = 'product-set-dialog__group';

        const selectedItemId = selections.get(String(group.id));
        const selectedItem = group.items.find(
          (item) => String(item.id) === String(selectedItemId)
        );

        const title = document.createElement('h3');
        title.className = 'product-set-dialog__group-title';
        title.id = `${this.dialog.id}-Group-${groupIndex}`;
        title.textContent = selectedItem?.title || group.title || '';
        section.setAttribute('aria-labelledby', title.id);

        const choices = document.createElement('div');
        choices.className = 'product-set-dialog__choices';
        choices.setAttribute('role', 'radiogroup');

        group.items.forEach((item) => {
          choices.append(this.createChoice(group, item, selections));
        });

        const hint = document.createElement('div');
        hint.className = 'product-set-dialog__group-hint';
        hint.setAttribute('aria-live', 'polite');
        hint.textContent = this.getGroupHint(group, selectedItem);
        hint.hidden = !hint.textContent;

        section.append(title, hint, choices);
        fragment.append(section);
      });

      this.groupsElement.replaceChildren(fragment);
      this.emptyElement.hidden = this.currentGroups.length > 0;
      this.querySelector('[data-product-set-confirm]').disabled = this.currentGroups.length === 0;
    }

    getGroupHint(group, item) {
      return item?.preorder && item.preorderMessage ? item.preorderMessage : (group.hint || '');
    }

    updateGroupSelection(groupId, itemId) {
      const groupKey = String(groupId);
      const itemKey = String(itemId);
      const group = this.currentGroups.find((candidate) => String(candidate.id) === groupKey);
      const selectedItem = group?.items.find((item) => String(item.id) === itemKey);
      let selectedButton = null;

      this.groupsElement.querySelectorAll('.product-set-dialog__choice').forEach((button) => {
        if (button.dataset.groupId !== groupKey) return;
        const selected = button.dataset.itemId === itemKey;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        const item = group?.items.find((candidate) => String(candidate.id) === button.dataset.itemId);
        if (item) this.updateChoicePrice(button, item);
        if (selected) selectedButton = button;
      });

      const title = selectedButton
        ?.closest('.product-set-dialog__group')
        ?.querySelector('.product-set-dialog__group-title');
      if (title) title.textContent = selectedItem?.title || group?.title || '';
      const hint = selectedButton
        ?.closest('.product-set-dialog__group')
        ?.querySelector('.product-set-dialog__group-hint');
      if (hint) {
        hint.textContent = this.getGroupHint(group, selectedItem);
        hint.hidden = !hint.textContent;
      }
    }

    createChoice(group, item, selections) {
      const selected = selections.get(String(group.id)) === String(item.id);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `product-set-dialog__choice${selected ? ' is-selected' : ''}`;
      button.dataset.groupId = String(group.id);
      button.dataset.itemId = String(item.id);
      button.dataset.quantity = String(this.getItemQuantity(item));
      button.dataset.unitPrice = (Math.max(0, Number(item.priceCents) || 0) / 100).toFixed(2);
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(selected));
      button.disabled = !item.available;

      if (item.preorder) {
        const badge = document.createElement('span');
        badge.className = 'product-set-dialog__choice-preorder';
        badge.textContent = 'Vorbestellung';
        button.append(badge);
      }

      const imageWrap = document.createElement('span');
      imageWrap.className = 'product-set-dialog__choice-image';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 160;
        image.height = 160;
        image.loading = 'lazy';
        image.decoding = 'async';
        imageWrap.append(image);
      }

      const copy = document.createElement('span');
      copy.className = 'product-set-dialog__choice-copy';

      const label = document.createElement('span');
      label.className = 'product-set-dialog__choice-label';
      label.textContent = item.title || '';
      copy.append(label);

      if (item.description) {
        const description = document.createElement('span');
        description.className = 'product-set-dialog__choice-description';
        description.textContent = item.description;
        copy.append(description);
      }

      const price = document.createElement('span');
      price.className = 'product-set-dialog__choice-price';
      copy.append(price);

      // 默认赠送项(0元, 无需变体)显示"已包含"标识 — 纯展示, 不影响任何提交逻辑
      if (item.isDefault) {
        const included = document.createElement('span');
        included.className = 'product-set-dialog__choice-included';
        included.textContent = 'Enthalten';
        copy.append(included);
      }

      if (!item.available) {
        const availability = document.createElement('span');
        availability.className = 'product-set-dialog__choice-availability';
        availability.textContent = 'Nicht verfügbar';
        copy.append(availability);
      }

      button.append(imageWrap, copy);
      const selectedItem = group.items.find(
        (candidate) => String(candidate.id) === String(selections.get(String(group.id)))
      );
      this.updateChoicePrice(button, item);
      return button;
    }

    updateChoicePrice(button, item) {
      const price = button.querySelector('.product-set-dialog__choice-price');
      if (!price) return;
      // 直接显示该项总价 = 单价 × 数量(每套), 带 + 前缀
      const total = Math.max(0, Number(item.priceCents) || 0) * this.getItemQuantity(item);
      price.hidden = false;
      // 0 元项(默认赠送)简洁显示 "+ 0 €", 其它金额保持两位小数
      price.textContent = total === 0 ? '+ 0 €' : `+ ${this.formatMoney(total)}`;
    }

    renderPreviews() {
      if (!this.previewsElement) return;
      const selected = this.getSelectedEntries();
      const fragment = document.createDocumentFragment();
      // 最多显示 3 个预览缩略图(桌面/移动一致), 超出部分折叠为 "+N" 徽章
      const maxVisible = 3;
      const visibleItems = selected.slice(0, maxVisible);

      visibleItems.forEach(({ item }) => {
        const preview = document.createElement('span');
        preview.className = 'product-set-overview__preview';
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.width = 108;
          image.height = 108;
          image.loading = 'lazy';
          image.decoding = 'async';
          preview.append(image);
        }
        fragment.append(preview);
      });

      if (selected.length > visibleItems.length) {
        const more = document.createElement('span');
        more.className = 'product-set-overview__preview product-set-overview__preview--more';
        more.textContent = `+${selected.length - visibleItems.length}`;
        fragment.append(more);
      }

      this.previewsElement.replaceChildren(fragment);
    }

    getPlusConfiguration(selections = this.committedSelections) {
      const entries = this.getSelectedEntries(selections);
      if (!this.initialised || !this.config.plusMode || !this.currentGroups.length || entries.length !== this.currentGroups.length || entries.some(({ item }) => !item.available || !Number.isSafeInteger(this.getItemQuantity(item)))) {
        throw new Error('Bitte wählen Sie eine verfügbare Zusammenstellung auf der Produktseite.');
      }
      return {
        schema: 1,
        currency: this.config.currency,
        totalCents: Number(this.config.basePriceCents) + this.getAddonCents(selections),
        items: entries.map(({ group, item }) => [String(group.id), String(item.id), this.getItemQuantity(item)]),
      };
    }

    syncFormFields() {
      if (!this.fieldsElement || !this.formId) return;
      const fragment = document.createDocumentFragment();

      const entries = this.getSelectedEntries().filter(({ group, item }) => group.title && item.title);
      const paidUpgrades = this.config.plusMode ? [] : this.getPaidUpgradeItems();
      const lineItemKeys = new Set(paidUpgrades.map((upgrade) => `${upgrade.groupId}:${upgrade.itemId}`));

      // 每组一条可见属性, 名为 "Zubehör N"(自动编号): 结账页/邮件逐行显示
      // 已有独立升级行的组不写, 避免结账页和商品行重复。plusMode 不生成升级行, 仍全写。
      let zubehorIndex = 0;
      entries.forEach(({ group, item }) => {
        if (lineItemKeys.has(`${String(group.id)}:${String(item.id)}`)) return;
        zubehorIndex += 1;
        const visible = document.createElement('input');
        visible.type = 'hidden';
        visible.name = `properties[Zubehör ${zubehorIndex}]`;
        visible.value = item.title;
        visible.setAttribute('form', this.formId);
        fragment.append(visible);
      });

      // 每组一条隐藏属性(_sm_cfg_ 前缀, 客户侧默认不显示):
      // 购物车模板点名渲染成"值"行, 数据留档供导出/API 查询
      entries.forEach(({ group, item }) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[_sm_cfg_${group.title}]`;
        input.value = item.title;
        input.setAttribute('form', this.formId);
        fragment.append(input);
      });

      paidUpgrades.forEach((upgrade) => {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'bundles';
        input.value = String(upgrade.id);
        input.checked = true;
        input.hidden = true;
        input.dataset.productSetUpgrade = 'true';
        input.dataset.quantityPerSet = String(upgrade.quantityPerSet);
        input.dataset.productSetGroup = upgrade.groupTitle || upgrade.groupId;
        input.dataset.productSetSelection = upgrade.itemTitle || upgrade.itemId;
        input.setAttribute('form', this.formId);
        fragment.append(input);
      });

      this.fieldsElement.replaceChildren(fragment);
    }

    updateSelectedOptionsText() {
      const values = this.variantOptions.filter((value, index) => index !== this.optionIndex && value);
      this.querySelectorAll('[data-product-set-selected-options]').forEach((element) => {
        element.textContent = values.join(' · ');
        element.classList.toggle('hidden', values.length === 0);
      });
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-product-set-dialog-open]');
      if (openButton) {
        this.openDialog(openButton);
        return;
      }

      if (event.target.closest('[data-product-set-close]')) {
        this.cancelDraft();
        return;
      }

      if (event.target === this.dialog) {
        this.cancelDraft();
        return;
      }

      const inlineMore = event.target.closest('[data-product-set-inline-more]');
      if (inlineMore) {
        this.toggleInlineMore(inlineMore);
        return;
      }

      const inlineChoice = event.target.closest('[data-product-set-inline-choice]');
      if (inlineChoice && !inlineChoice.disabled) {
        this.commitInlineChoice(inlineChoice.dataset.groupId, inlineChoice.dataset.itemId);
        return;
      }

      const choice = event.target.closest('.product-set-dialog__choice');
      if (choice && !choice.disabled) {
        this.dialog.querySelector('[data-product-set-error]')?.remove();
        this.draftSelections.set(choice.dataset.groupId, choice.dataset.itemId);
        this.updateGroupSelection(choice.dataset.groupId, choice.dataset.itemId);
        this.setAddonCents(this.getAddonCents(this.draftSelections));
        return;
      }

      if (event.target.closest('[data-product-set-confirm]')) this.commitDraft();
    }

    openDialog(trigger) {
      if (!this.dialog.showModal || this.currentGroups.length === 0) return;
      this.activeTrigger = trigger;
      this.dialog.querySelector('[data-product-set-error]')?.remove();
      this.draftSelections = new Map(this.committedSelections);
      this.renderGroups(this.draftSelections);
      trigger.setAttribute('aria-expanded', 'true');
      this.dialog.showModal();

      // showModal() 按规范会自动聚焦弹窗内第一个可聚焦元素（即右上角关闭按钮），
      // 在触屏上会渲染出焦点圆圈。改为聚焦标题（无 outline 样式，屏幕阅读器仍能
      // 正确播报弹窗）；键盘用户 Tab 到关闭按钮时依旧保留焦点环。
      const title = this.dialog.querySelector('.product-set-dialog__title');
      if (title) {
        title.tabIndex = -1;
        title.focus({ preventScroll: true });
      }
    }

    commitDraft() {
      if (this.config.plusMode) {
        try {
          this.getPlusConfiguration(this.draftSelections);
        } catch (error) {
          let message = this.dialog.querySelector('[data-product-set-error]');
          if (!message) {
            message = document.createElement('p');
            message.dataset.productSetError = '';
            message.className = 'alert alert--error';
            message.setAttribute('role', 'alert');
            this.groupsElement.after(message);
          }
          message.textContent = error.message;
          return;
        }
        this.dialog.querySelector('[data-product-set-error]')?.remove();
      }
      this.committedSelections = this.sanitiseSelections(this.draftSelections);
      this.draftSelections = new Map(this.committedSelections);
      this.renderPreviews();
      this.renderInlinePanel();
      this.syncFormFields();
      this.setAddonCents(this.getAddonCents(this.committedSelections));
      this.dispatchEvent(new CustomEvent('product-set:change', {
        bubbles: true,
        detail: { selections: this.getSelectedEntries() }
      }));
      this.dialog.close('confirm');
    }

    cancelDraft() {
      this.draftSelections = new Map(this.committedSelections);
      this.renderGroups(this.committedSelections);
      this.setAddonCents(this.getAddonCents(this.committedSelections));
      if (this.dialog.open) this.dialog.close('cancel');
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.cancelDraft();
    }

    handleDialogClose() {
      this.activeTrigger?.setAttribute('aria-expanded', 'false');
      this.activeTrigger?.focus({ preventScroll: true });
      this.activeTrigger = null;
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      const previousLeistung = this.leistungValue;
      this.basePriceCents = Number(variant.price) || 0;
      this.config.selectedVariantId = variant.id;
      this.config.basePriceCents = Number(variant.price) || 0;
      this.variantOptions = this.extractVariantOptions(variant);
      this.applyCountryVariants();
      this.activateProfile({ reset: previousLeistung !== this.leistungValue });
    }

    extractVariantOptions(variant) {
      if (Array.isArray(variant.options) && variant.options.length === this.config.optionNames.length) {
        return variant.options.slice();
      }

      const legacyOptions = this.config.optionNames.map(
        (_, index) => variant[`option${index + 1}`]
      );
      if (legacyOptions.every((value) => value != null)) return legacyOptions;

      const pickerOptions = Array.from(
        this.productInfo?.querySelectorAll('variant-selects fieldset') || []
      ).map((fieldset) => fieldset.querySelector('input:checked, option:checked')?.value || '');
      return pickerOptions.length === this.config.optionNames.length
        ? pickerOptions
        : this.variantOptions.slice();
    }

    handlePriceUpdated(event) {
      const detail = event.detail || {};
      if (detail.root && detail.root !== this) return;
      if (Number.isFinite(detail.baseCents)) this.basePriceCents = detail.baseCents;
      if (Number.isFinite(detail.addonCents)) {
        const ownAddon = Number(detail.sources?.['product-set']) || 0;
        this.otherAddonCents = Math.max(0, detail.addonCents - ownAddon);
      }
      if (Number.isFinite(detail.totalCents)) this.updateTotal(detail.totalCents);
    }

    setAddonCents(cents) {
      const safeCents = Math.max(0, Number(cents) || 0);
      this.dataset.addonCents = String(safeCents);
      this.updateTotal(this.basePriceCents + this.otherAddonCents + safeCents);
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateTotal(cents) {
      const formatted = this.formatMoney(cents);
      this.totalElements.forEach((element) => { element.textContent = formatted; });
    }

    formatMoney(cents) {
      return this.moneyFormatter.format((Number(cents) || 0) / 100);
    }

  }

  if (!customElements.get('product-set-configurator')) {
    customElements.define('product-set-configurator', ProductSetConfigurator);
  }
})();
